import { useEffect } from 'react';
import {
  captureException,
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  withScope,
} from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { CaptureApiErrorContext } from './types';
import { createApiError, describeApiError, isAxiosError } from './utils';

export { captureException, withScope };

export const captureApiError = (
  error: unknown,
  context?: CaptureApiErrorContext,
) => {
  if (!isAxiosError(error)) {
    withScope((scope) => {
      if (context?.queryKey) {
        scope.setTag('queryKey', context.queryKey);
      }
      captureException(error);
    });
    return;
  }

  const { method, url, status, message } = describeApiError(error);
  const apiError = createApiError(error, message);

  withScope((scope) => {
    scope.setFingerprint(['api-error', method, url, String(status)]);
    scope.setTag('http.method', method);
    scope.setTag('http.url', url);
    scope.setTag('http.status_code', status);

    if (context?.queryKey) {
      scope.setTag('queryKey', context.queryKey);
    }

    if (error.response) {
      scope.setContext('response', {
        status: error.response.status,
        headers: error.response.headers,
        data: error.response.data,
      });
    }

    captureException(apiError);
  });
};

export const initializeSentry = () => {
  if (
    import.meta.env.VITE_SENTRY_DSN &&
    import.meta.env.VITE_CLAIMSCORE_ENV === 'prod'
  ) {
    init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        replayIntegration(),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['localhost'],
      replaysOnErrorSampleRate: 1.0,
    });
  }
};
